import React, { useState, useEffect } from 'react';
import { 
  FiSettings, 
  FiDollarSign, 
  FiUser, 
  FiShield, 
  FiBell, 
  FiSave,
  FiRefreshCw,
  FiCheck
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { buscarConfiguracoesFinanceirasDoBanco, salvarConfiguracoesFinanceirasBanco } from '../utils/configuracoes';
import { useAuth } from '../contexts/AuthContext';
import styles from './Configuracoes.module.css';

const Configuracoes = () => {
  const { hasPermission, getPermissionScope } = useAuth();
  const [activeTab, setActiveTab] = useState('financeiro');
  const [loading, setLoading] = useState(true); // Iniciar como loading para buscar dados
  const [configuracoes, setConfiguracoes] = useState({
    valorConsulta: '0.00', // Iniciar vazio, será preenchido do banco
    valorRetorno: '0.00',  // Iniciar vazio, será preenchido do banco  
    valorExame: '0.00',    // Iniciar vazio, será preenchido do banco
    nomeClinica: 'Lumi One Clínica',
    emailClinica: 'contato@lumione.com.br',
    telefoneClinica: '(11) 99999-9999',
    enderecoClinica: 'Rua das Flores, 123 - Centro',
    notificacaoEstoque: true,
    notificacaoConsultas: true,
    notificacaoVencimento: true,
    backupAutomatico: true
  });

  const [alteracoesSalvas, setAlteracoesSalvas] = useState(false);

  // Carregar configurações sempre que o componente for montado
  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  // Recarregar configurações quando a aba ativa mudar
  useEffect(() => {
    if (activeTab === 'financeiro') {
      carregarConfiguracoes();
    }
  }, [activeTab]);

  const carregarConfiguracoes = async () => {
    try {
      setLoading(true);
      console.log('🔄 Iniciando carregamento das configurações...');
      
      // Buscar configurações financeiras do banco (sempre fresh)
      console.log('🔍 Buscando configurações do banco (sem cache)...');
      const configsFinanceirasBanco = await buscarConfiguracoesFinanceirasDoBanco(true);
      console.log('💰 Configurações financeiras do banco:', configsFinanceirasBanco);
      console.log('💰 Tipo da resposta:', typeof configsFinanceirasBanco);
      console.log('💰 Chaves disponíveis:', Object.keys(configsFinanceirasBanco));
      
      // Buscar outras configurações do localStorage
      const configSalvas = localStorage.getItem('configuracoes_sistema');
      let outrasConfigs = {};
      if (configSalvas) {
        outrasConfigs = JSON.parse(configSalvas);
        console.log('📱 Configurações do localStorage:', outrasConfigs);
      } else {
        console.log('📱 Nenhuma configuração no localStorage');
      }
      
      // Preparar valores financeiros
      const valorConsultaBanco = configsFinanceirasBanco.valor_consulta !== undefined ? configsFinanceirasBanco.valor_consulta : 0.00;
      const valorRetornoBanco = configsFinanceirasBanco.valor_retorno !== undefined ? configsFinanceirasBanco.valor_retorno : 0.00;
      const valorExameBanco = configsFinanceirasBanco.valor_exame !== undefined ? configsFinanceirasBanco.valor_exame : 0.00;
      
      console.log('🔢 Valores processados do banco:', {
        valorConsulta: valorConsultaBanco,
        valorRetorno: valorRetornoBanco,
        valorExame: valorExameBanco
      });
      
      // Mesclar configurações: BANCO tem prioridade absoluta para valores financeiros
      const novasConfiguracoes = {
        // 1. Outras configurações padrão
        nomeClinica: 'Lumi One Clínica',
        emailClinica: 'contato@lumione.com.br',
        telefoneClinica: '(11) 99999-9999',
        enderecoClinica: 'Rua das Flores, 123 - Centro',
        notificacaoEstoque: true,
        notificacaoConsultas: true,
        notificacaoVencimento: true,
        backupAutomatico: true,
        
        // 2. Outras configurações do localStorage (sobrescreve padrões)
        ...outrasConfigs,
        
        // 3. Valores financeiros SEMPRE DO BANCO (nunca usar padrão)
        valorConsulta: valorConsultaBanco.toString(),
        valorRetorno: valorRetornoBanco.toString(),
        valorExame: valorExameBanco.toString()
      };
      
      console.log('✅ Configurações finais que serão definidas:', novasConfiguracoes);
      console.log('🎯 Valores financeiros finais:', {
        valorConsulta: novasConfiguracoes.valorConsulta,
        valorRetorno: novasConfiguracoes.valorRetorno,
        valorExame: novasConfiguracoes.valorExame
      });
      
      setConfiguracoes(novasConfiguracoes);
      
      // Verificar se realmente foi definido
      setTimeout(() => {
        console.log('🔍 Estado após setConfiguracoes:', configuracoes);
      }, 100);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações do banco. Verifique se está logado.');
      
      // Em caso de erro, usar apenas configurações do localStorage (sem valores financeiros)
      const configSalvas = localStorage.getItem('configuracoes_sistema');
      if (configSalvas) {
        const outrasConfigs = JSON.parse(configSalvas);
        setConfiguracoes(prev => ({
          ...prev,
          ...outrasConfigs
          // Manter valores financeiros em 0 se não conseguir buscar do banco
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const salvarConfiguracoes = async () => {
    try {
      setLoading(true);
      
      // Validações - aceita valores zero ou maiores
      if (isNaN(parseFloat(configuracoes.valorConsulta)) || parseFloat(configuracoes.valorConsulta) < 0) {
        toast.error('Valor da consulta deve ser um número válido (zero ou maior)');
        return;
      }
      
      if (isNaN(parseFloat(configuracoes.valorRetorno)) || parseFloat(configuracoes.valorRetorno) < 0) {
        toast.error('Valor do retorno deve ser um número válido (zero ou maior)');
        return;
      }
      
      if (isNaN(parseFloat(configuracoes.valorExame)) || parseFloat(configuracoes.valorExame) < 0) {
        toast.error('Valor do exame deve ser um número válido (zero ou maior)');
        return;
      }
      
      // Salvar configurações financeiras no banco
      console.log('💾 Salvando configurações financeiras no banco...');
      const sucessoFinanceiro = await salvarConfiguracoesFinanceirasBanco({
        valorConsulta: parseFloat(configuracoes.valorConsulta),
        valorRetorno: parseFloat(configuracoes.valorRetorno),
        valorExame: parseFloat(configuracoes.valorExame)
      });
      
      if (!sucessoFinanceiro) {
        toast.error('Erro ao salvar valores financeiros no banco');
        return;
      }
      
      console.log('✅ Configurações financeiras salvas no banco!');
      
      // Salvar outras configurações no localStorage
      const outrasConfiguracoes = { ...configuracoes };
      delete outrasConfiguracoes.valorConsulta;
      delete outrasConfiguracoes.valorRetorno;
      delete outrasConfiguracoes.valorExame;
      localStorage.setItem('configuracoes_sistema', JSON.stringify(outrasConfiguracoes));
      
      setAlteracoesSalvas(true);
      setTimeout(() => setAlteracoesSalvas(false), 3000);
      
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (campo, valor) => {
    setConfiguracoes(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const resetarConfiguracoes = () => {
    setConfiguracoes({
      valorConsulta: '80.00',
      valorRetorno: '60.00',
      valorExame: '120.00',
      nomeClinica: 'Lumi One Clínica',
      emailClinica: 'contato@lumione.com.br',
      telefoneClinica: '(11) 99999-9999',
      enderecoClinica: 'Rua das Flores, 123 - Centro',
      notificacaoEstoque: true,
      notificacaoConsultas: true,
      notificacaoVencimento: true,
      backupAutomatico: true
    });
    toast.info('Configurações resetadas para os valores padrão');
  };

  const allTabs = [
    { 
      id: 'financeiro', 
      label: 'Financeiro', 
      icon: FiDollarSign,
      requiredPermissions: ['configuracoes:view_financeiras', 'configuracoes:edit_financeiras']
    }
  ];

  // Filtrar abas por permissão
  const tabs = allTabs.filter(tab => 
    tab.requiredPermissions.some(permission => hasPermission(permission))
  );

  // Ajustar aba ativa se a atual não estiver disponível
  useEffect(() => {
    if (tabs.length > 0 && !tabs.some(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  // Funções para verificar permissões específicas
  const canEditFinanceiro = () => {
    return hasPermission('configuracoes:edit_financeiras');
  };

  const canViewFinanceiro = () => {
    return hasPermission('configuracoes:view_financeiras') || hasPermission('configuracoes:edit_financeiras');
  };

  // Verificar se tem pelo menos uma permissão de configurações
  const hasAnyConfigPermission = () => {
    return hasPermission('configuracoes:view_financeiras') || 
           hasPermission('configuracoes:edit_financeiras');
  };

  // Verificar permissões
  if (!hasAnyConfigPermission()) {
    return (
      <div className={styles.noPermission}>
        <h2>Acesso Negado</h2>
        <p>Você não tem permissão para acessar as configurações.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            <FiSettings />
            Configurações
          </h1>
          <p className={styles.subtitle}>
            Gerencie as configurações do sistema e preferências
          </p>
        </div>

        <div className={styles.headerActions}>
          <button 
            onClick={resetarConfiguracoes}
            className={`${styles.button} ${styles.secondary}`}
            disabled={loading}
          >
            <FiRefreshCw />
            Resetar
          </button>
          
          {canEditFinanceiro() && (
            <button 
              onClick={salvarConfiguracoes}
              className={`${styles.button} ${styles.primary}`}
              disabled={loading}
            >
              {loading ? <FiRefreshCw className={styles.spinning} /> : <FiSave />}
              {alteracoesSalvas ? <FiCheck /> : 'Salvar'}
            </button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        {/* Tabs de navegação */}
        <div className={styles.tabs}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
              >
                <Icon />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Conteúdo das abas */}
        <div className={styles.tabContent}>
          
          {/* Aba Financeiro */}
          {activeTab === 'financeiro' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3>Configurações Financeiras</h3>
                {canEditFinanceiro() ? (
                  <p>Configure os valores padrão para consultas, exames e outros procedimentos.</p>
                ) : (
                  <p className={styles.readOnlyMessage}>
                    📖 Você tem permissão apenas para visualizar estas configurações financeiras.
                  </p>
                )}
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FiDollarSign />
                    Valor da Consulta (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={configuracoes.valorConsulta || '0.00'}
                    onChange={(e) => handleInputChange('valorConsulta', e.target.value)}
                    className={`${styles.input} ${!canEditFinanceiro() ? styles.readonly : ''}`}
                    placeholder="0.00"
                    readOnly={!canEditFinanceiro()}
                    disabled={!canEditFinanceiro()}
                  />
                  <small className={styles.hint}>
                    Valor padrão cobrado por consulta médica
                  </small>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FiDollarSign />
                    Valor do Retorno (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={configuracoes.valorRetorno || '0.00'}
                    onChange={(e) => handleInputChange('valorRetorno', e.target.value)}
                    className={`${styles.input} ${!canEditFinanceiro() ? styles.readonly : ''}`}
                    placeholder="0.00"
                    readOnly={!canEditFinanceiro()}
                    disabled={!canEditFinanceiro()}
                  />
                  <small className={styles.hint}>
                    Valor cobrado para consultas de retorno
                  </small>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FiDollarSign />
                    Valor do Exame (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={configuracoes.valorExame || '0.00'}
                    onChange={(e) => handleInputChange('valorExame', e.target.value)}
                    className={`${styles.input} ${!canEditFinanceiro() ? styles.readonly : ''}`}
                    placeholder="0.00"
                    readOnly={!canEditFinanceiro()}
                    disabled={!canEditFinanceiro()}
                  />
                  <small className={styles.hint}>
                    Valor padrão para exames básicos
                  </small>
                </div>
              </div>

              <div className={styles.previewCard}>
                <h4>Prévia dos Valores</h4>
                <div className={styles.previewGrid}>
                  <div className={styles.previewItem}>
                    <span>Consulta:</span>
                    <strong>R$ {parseFloat(configuracoes.valorConsulta || 0).toFixed(2)}</strong>
                  </div>
                  <div className={styles.previewItem}>
                    <span>Retorno:</span>
                    <strong>R$ {parseFloat(configuracoes.valorRetorno || 0).toFixed(2)}</strong>
                  </div>
                  <div className={styles.previewItem}>
                    <span>Exame:</span>
                    <strong>R$ {parseFloat(configuracoes.valorExame || 0).toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aba Dados da Clínica */}
          {activeTab === 'clinica' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3>Dados da Clínica</h3>
                <p>Informações básicas da clínica que aparecerão nos relatórios e documentos.</p>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Nome da Clínica</label>
                  <input
                    type="text"
                    value={configuracoes.nomeClinica}
                    onChange={(e) => handleInputChange('nomeClinica', e.target.value)}
                    className={styles.input}
                    placeholder="Lumi One Clínica"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>E-mail de Contato</label>
                  <input
                    type="email"
                    value={configuracoes.emailClinica}
                    onChange={(e) => handleInputChange('emailClinica', e.target.value)}
                    className={styles.input}
                    placeholder="contato@lumione.com.br"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Telefone</label>
                  <input
                    type="tel"
                    value={configuracoes.telefoneClinica}
                    onChange={(e) => handleInputChange('telefoneClinica', e.target.value)}
                    className={styles.input}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Endereço</label>
                  <input
                    type="text"
                    value={configuracoes.enderecoClinica}
                    onChange={(e) => handleInputChange('enderecoClinica', e.target.value)}
                    className={styles.input}
                    placeholder="Rua das Flores, 123 - Centro"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Aba Notificações */}
          {activeTab === 'notificacoes' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3>Configurações de Notificações</h3>
                <p>Configure quais notificações você deseja receber.</p>
              </div>

              <div className={styles.togglesList}>
                <div className={styles.toggleItem}>
                  <div className={styles.toggleInfo}>
                    <strong>Alertas de Estoque</strong>
                    <p>Notificações quando medicamentos estiverem com estoque baixo</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={configuracoes.notificacaoEstoque}
                      onChange={(e) => handleInputChange('notificacaoEstoque', e.target.checked)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.toggleItem}>
                  <div className={styles.toggleInfo}>
                    <strong>Lembretes de Consulta</strong>
                    <p>Notificações sobre consultas agendadas para hoje</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={configuracoes.notificacaoConsultas}
                      onChange={(e) => handleInputChange('notificacaoConsultas', e.target.checked)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.toggleItem}>
                  <div className={styles.toggleInfo}>
                    <strong>Alertas de Vencimento</strong>
                    <p>Notificações sobre medicamentos próximos ao vencimento</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={configuracoes.notificacaoVencimento}
                      onChange={(e) => handleInputChange('notificacaoVencimento', e.target.checked)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Aba Sistema */}
          {activeTab === 'sistema' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3>Configurações do Sistema</h3>
                <p>Configurações avançadas e de segurança do sistema.</p>
              </div>

              <div className={styles.togglesList}>
                <div className={styles.toggleItem}>
                  <div className={styles.toggleInfo}>
                    <strong>Backup Automático</strong>
                    <p>Realiza backup automático dos dados diariamente</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={configuracoes.backupAutomatico}
                      onChange={(e) => handleInputChange('backupAutomatico', e.target.checked)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>

              <div className={styles.systemInfo}>
                <h4>Informações do Sistema</h4>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <strong>Versão:</strong>
                    <span>1.0.0</span>
                  </div>
                  <div className={styles.infoItem}>
                    <strong>Última atualização:</strong>
                    <span>06/11/2025</span>
                  </div>
                  <div className={styles.infoItem}>
                    <strong>Banco de dados:</strong>
                    <span>MySQL 8.0</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Configuracoes;